/**
 * IPASS signout parser.
 *
 * Parses Casey's flow-mode output for IPASS section markers and LOC data.
 * Ported from JSX codebase (master:frontend/src/utils/ipassParser.js)
 * to TypeScript with full type definitions.
 *
 * Markers:
 *   <!-- IPASS:I --> ... content ... <!-- /IPASS:I -->
 *   <!-- IPASS:P --> ... content ... <!-- /IPASS:P -->
 *   <!-- IPASS:VD --> ... content ... <!-- /IPASS:VD -->
 *   <!-- IPASS:A --> ... content ... <!-- /IPASS:A -->
 *   <!-- IPASS:S1 --> ... content ... <!-- /IPASS:S1 -->
 *   <!-- IPASS:S2 --> ... content ... <!-- /IPASS:S2 -->
 *   <!-- LOC: { "disposition": "...", "telemetry": "...", "unit": "...", "rationale": "..." } -->
 */
import { marked } from 'marked'

// Ensure GFM task lists (- [ ] checkboxes) are parsed
marked.use({ gfm: true, breaks: true })

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type IPASSSectionKey = 'I' | 'S2' | 'VD' | 'A' | 'S1' | 'P'

export type IPASSSections = Partial<Record<IPASSSectionKey, string>>

export interface LOCData {
  disposition: string
  telemetry: string
  icuCriteriaMet: string
  unit: string
  nextAction: string
  rationale: string
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

export const IPASS_SECTIONS: IPASSSectionKey[] = ['I', 'S2', 'VD', 'A', 'S1', 'P']

export const SECTION_LABELS: Record<IPASSSectionKey, string> = {
  I: 'I \u2014 Illness Severity',
  S2: 'S \u2014 Patient Summary',
  VD: 'V/D \u2014 Vitals & Diagnostics',
  A: 'A \u2014 Action List',
  S1: 'S \u2014 Situation Awareness',
  P: 'P \u2014 Clinical Narrative',
}

export const SECTION_COLORS: Record<IPASSSectionKey, string> = {
  I: '#f87171',   // red
  S2: '#4ade80',  // green
  VD: '#2dd4bf',  // teal
  A: '#fb923c',   // orange
  S1: '#fbbf24',  // amber
  P: '#60a5fa',   // blue
}

// ────────────────────────────────────────────────────────
// parseIPASS
// ────────────────────────────────────────────────────────

/**
 * Extract IPASS sections from agent output.
 * Falls back to header-based parsing if no markers found.
 */
export const parseIPASS = (output: string | null | undefined): IPASSSections => {
  if (!output) return {}
  const sections: IPASSSections = {}

  for (const key of IPASS_SECTIONS) {
    const regex = new RegExp(
      `<!--\\s*IPASS:${key}\\s*-->([\\s\\S]*?)<!--\\s*/IPASS:${key}\\s*-->`,
      'i',
    )
    const match = output.match(regex)
    if (match && match[1].trim()) {
      sections[key] = match[1].trim()
    }
  }

  // Fallback: if no markers found, try to parse by header patterns
  if (Object.keys(sections).length === 0) {
    const fallback = parseIPASSByHeaders(output)
    Object.assign(sections, fallback)
  }

  return sections
}

/**
 * Fallback: parse IPASS from markdown headers when markers aren't used.
 */
const parseIPASSByHeaders = (output: string): IPASSSections => {
  const sections: IPASSSections = {}
  const patterns: Array<{ key: IPASSSectionKey; regex: RegExp }> = [
    { key: 'I', regex: /(?:#{1,3}\s*)?(?:I\s*[\u2014\u2013-]\s*)?Illness\s+Severity[:\s]*([\s\S]*?)(?=(?:#{1,3}\s*)?(?:[SPVA]\s*[\u2014\u2013-]\s*)?(?:Patient\s+Summary|Clinical\s+Narrative|Vitals|Action|Situation)|$)/i },
    { key: 'S2', regex: /(?:#{1,3}\s*)?(?:S\s*[\u2014\u2013-]\s*)?Patient\s+Summary[:\s]*([\s\S]*?)(?=(?:#{1,3}\s*)?(?:[VA]\s*[\u2014\u2013-]\s*)?(?:Vitals|Action|Situation|Clinical\s+Narrative)|$)/i },
    { key: 'VD', regex: /(?:#{1,3}\s*)?(?:V\/D\s*[\u2014\u2013-]\s*)?Vitals\s*(?:&|and)\s*Diagnostics[:\s]*([\s\S]*?)(?=(?:#{1,3}\s*)?(?:[AS]\s*[\u2014\u2013-]\s*)?(?:Action|Situation|Clinical\s+Narrative)|$)/i },
    { key: 'A', regex: /(?:#{1,3}\s*)?(?:A\s*[\u2014\u2013-]\s*)?Action\s+List[:\s]*([\s\S]*?)(?=(?:#{1,3}\s*)?(?:[SP]\s*[\u2014\u2013-]\s*)?(?:Situation|Clinical\s+Narrative)|$)/i },
    { key: 'S1', regex: /(?:#{1,3}\s*)?(?:S\s*[\u2014\u2013-]\s*)?Situation\s+Awareness[:\s]*([\s\S]*?)(?=(?:#{1,3}\s*)?(?:P\s*[\u2014\u2013-]\s*)?Clinical\s+Narrative|$)/i },
    { key: 'P', regex: /(?:#{1,3}\s*)?(?:P\s*[\u2014\u2013-]\s*)?Clinical\s+Narrative[:\s]*([\s\S]*?)$/i },
  ]

  for (const { key, regex } of patterns) {
    const match = output.match(regex)
    if (match && match[1].trim().length > 10) {
      sections[key] = match[1].trim()
    }
  }

  return sections
}

// ────────────────────────────────────────────────────────
// parseLOC
// ────────────────────────────────────────────────────────

/**
 * Parse LOC (Level of Care) data from output.
 */
export const parseLOC = (output: string | null | undefined): LOCData | null => {
  if (!output) return null
  const match = output.match(/<!--\s*LOC:\s*(\{[\s\S]*?\})\s*-->/)
  if (!match) return null
  try {
    const data = JSON.parse(match[1])
    return {
      disposition: data.disposition || 'Unknown',
      telemetry: data.telemetry || 'Unknown',
      icuCriteriaMet: data.icuCriteriaMet || 'no',
      unit: data.unit || 'Unknown',
      nextAction: data.nextAction || '',
      rationale: data.rationale || '',
    }
  } catch (err) {
    console.warn('[ipassParser] Failed to parse disposition JSON:', err)
    return null
  }
}

// ────────────────────────────────────────────────────────
// mergeIPASS
// ────────────────────────────────────────────────────────

/**
 * Merge new IPASS sections into an existing document.
 * Preserves previously populated sections, overwrites matched keys.
 */
export const mergeIPASS = (existing: IPASSSections, newSections: IPASSSections): IPASSSections => {
  return { ...existing, ...newSections }
}

// ────────────────────────────────────────────────────────
// renderIPASSHtml
// ────────────────────────────────────────────────────────

/**
 * Render the VD (Vitals & Diagnostics) section as rich clinical HTML.
 * Parses markdown bullets into structured sub-sections.
 */
const renderVDTabular = (content: string): string => {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean)

  const subsections: Record<string, string[]> = { vitals: [], labs: [], imaging: [], other: [] }
  let currentBucket: keyof typeof subsections = 'other'

  const VITAL_RE = /^(T\b|HR\b|BP\b|RR\b|SpO2|Temp\b|Pulse\b|MAP\b|O2\b|Wt\b|Weight\b|BMI\b)/i
  const LAB_RE = /^(WBC|Hgb|Hb\b|Plt|Na\b|K\b|Cl\b|CO2\b|Cr\b|BUN|Glucose|Glu\b|Lactate|BNP|Troponin|Trop\b|Procal|pH\b|pCO2|pO2|HCO3|Mg\b|Ca\b|Phos|INR|AST|ALT|Bili|Tbili|Dbili|Albumin|Alb\b|A1c|TSH|Lipase|D-dimer|UA\b|TP\b|Alk\b|GFR|SG\b|urobilinogen)/i
  const DIAG_RE = /^(CXR|CT\b|MRI|X-ray|US\b|Echo|EKG|ECG|ABG\b|Ultrasound|RUQ|LUQ|KUB|HIDA)/i
  const PANEL_RE = /^(BMPs?|CBCs?|CMPs?|LFTs?|Coags?|ABGs?|VBGs?|BGAs?|Hepatic|Renal|UA)\b/i
  const HEADER_VITALS_RE = /^[-\u2013\u2022*]*\s*\**\s*(latest\s+)?vitals\b/i
  const HEADER_LABS_RE = /^[-\u2013\u2022*]*\s*\**\s*(key\s+|pertinent\s+)?labs\b/i
  const HEADER_DIAG_RE = /^[-\u2013\u2022*]*\s*\**\s*(imaging|radiology|diagnostics|prior\s+imaging)\b/i

  const classifyTokens = (data: string, fallbackBucket: keyof typeof subsections) => {
    const tokens = data.split(/[;,]\s*(?![^()]*\))/).map(t => t.trim()).filter(Boolean)
    for (const tok of tokens) {
      if (VITAL_RE.test(tok)) subsections.vitals.push(tok)
      else if (LAB_RE.test(tok)) subsections.labs.push(tok)
      else if (DIAG_RE.test(tok)) subsections.imaging.push(tok)
      else if (tok.length > 1) subsections[fallbackBucket].push(tok)
    }
  }

  for (const line of lines) {
    const stripped = line.replace(/^[-\u2013\u2022*]\s*/, '').replace(/\*\*/g, '').replace(/\*/g, '').trim()
    if (!stripped) continue

    let headerMatch: RegExpMatchArray | null

    if ((headerMatch = stripped.match(/^(latest\s+)?vitals\b\s*(?:\([^)]*\))?\s*:\s*(.+)$/i))) {
      currentBucket = 'vitals'
      classifyTokens(headerMatch[2], 'vitals')
      continue
    }
    if ((headerMatch = stripped.match(/^(key\s+|pertinent\s+)?labs\b\s*(?:\([^)]*\))?\s*:\s*(.+)$/i))) {
      currentBucket = 'labs'
      classifyTokens(headerMatch[2], 'labs')
      continue
    }
    if ((headerMatch = stripped.match(/^(imaging|radiology|diagnostics)\b\s*(?:\([^)]*\))?\s*:\s*(.+)$/i))) {
      currentBucket = 'imaging'
      classifyTokens(headerMatch[2], 'imaging')
      continue
    }

    if (HEADER_VITALS_RE.test(line)) { currentBucket = 'vitals'; continue }
    if (HEADER_LABS_RE.test(line)) { currentBucket = 'labs'; continue }
    if (HEADER_DIAG_RE.test(line)) { currentBucket = 'imaging'; continue }

    const panelMatch = stripped.match(/^([A-Za-z/]+)\s*(?:\([^)]*\))?\s*:\s*(.+)$/)
    if (panelMatch && PANEL_RE.test(panelMatch[1]) && currentBucket !== 'imaging') {
      classifyTokens(panelMatch[2], 'labs')
      continue
    }

    if (currentBucket === 'imaging' && DIAG_RE.test(stripped)) {
      subsections.imaging.push(stripped)
    } else if (VITAL_RE.test(stripped)) {
      classifyTokens(stripped, 'vitals')
    } else if (LAB_RE.test(stripped)) {
      classifyTokens(stripped, 'labs')
    } else if (DIAG_RE.test(stripped)) {
      subsections.imaging.push(stripped)
    } else {
      classifyTokens(stripped, currentBucket)
    }
  }

  if (subsections.vitals.length === 0 && subsections.labs.length === 0 && subsections.other.length > 0) {
    const allText = subsections.other.join(' ')
    subsections.other = []
    classifyTokens(allText, 'other')
  }

  const splitKV = (str: string): [string, string] => {
    const clean = str.replace(/\s*\(\d{1,2}\/\d{1,2}[^)]*\)/g, '').trim()
    const colonMatch = clean.match(/^([^:=]+?)\s*[:=]\s*(.+)$/)
    if (colonMatch) return [colonMatch[1].trim(), colonMatch[2].trim()]
    const numMatch = clean.match(/^(.+?)\s+([\d<>\u2265\u2264].*)$/)
    if (numMatch) return [numMatch[1].trim(), numMatch[2].trim()]
    return [clean, '']
  }

  const vitalIcon = (label: string): string => {
    const l = label.toLowerCase().replace(/[^a-z0-9\u2082]/g, '')
    if (l === 't' || l === 'temp') return '\uD83C\uDF21'
    if (l === 'hr' || l === 'pulse') return '\u2665'
    if (l === 'bp') return '\uD83E\uDE78'
    if (l === 'rr') return '\uD83E\uDEC1'
    if (l === 'spo2' || l === 'spo\u2082' || l === 'o2') return 'O\u2082'
    if (l === 'map') return '\u25CE'
    if (l === 'wt' || l === 'weight') return '\u2696'
    return ''
  }

  const vitalLabel = (label: string): string => {
    const l = label.toLowerCase().replace(/[^a-z0-9]/g, '')
    const map: Record<string, string> = {
      t: 'Temp', temp: 'Temp', temperature: 'Temp', hr: 'HR', pulse: 'HR',
      bp: 'BP', rr: 'RR', spo2: 'SpO\u2082', o2: 'SpO\u2082', map: 'MAP',
      wt: 'Wt', weight: 'Wt', bmi: 'BMI',
    }
    return map[l] || label
  }

  interface KVItem {
    label: string
    value: string
    icon?: string
  }

  const mergeOrphans = (items: KVItem[]): KVItem[] => {
    const merged: KVItem[] = []
    const ORPHANS = /^(ra|room\s*air|nc|nrb|hfnc|kg|lbs?|cm|in|on|l\/min|lpm|bpm|mmhg)$/i
    for (const item of items) {
      if (/^\d+\)?$/.test(item.label) && !item.value) continue
      if (!item.value && ORPHANS.test(item.label) && merged.length > 0) {
        merged[merged.length - 1].value += ` ${item.label}`
        continue
      }
      merged.push(item)
    }
    return merged
  }

  const labValueColor = (label: string, value: string): string | null => {
    const num = parseFloat(value)
    if (isNaN(num)) return null
    const l = label.toLowerCase().replace(/[^a-z0-9]/g, '')
    const ranges: Record<string, [number, number, number, number]> = {
      wbc: [2, 4.5, 11, 30], hgb: [7, 12, 17.5, 20], hb: [7, 12, 17.5, 20],
      plt: [50, 150, 400, 1000], na: [120, 136, 145, 155], k: [2.5, 3.5, 5.0, 6.5],
      cl: [80, 98, 106, 120], co2: [10, 22, 29, 40], bun: [0, 7, 20, 100],
      cr: [0, 0.6, 1.3, 4], glu: [40, 70, 100, 500], glucose: [40, 70, 100, 500],
      ca: [6, 8.5, 10.5, 14], mg: [1, 1.7, 2.2, 5], phos: [1.5, 2.5, 4.5, 7],
      ast: [0, 0, 40, 200], alt: [0, 0, 56, 200], alk: [0, 44, 147, 500],
      tbili: [0, 0, 1.2, 5], dbili: [0, 0, 0.3, 2], alb: [2, 3.5, 5.5, 7],
      albumin: [2, 3.5, 5.5, 7], tp: [3, 6, 8.3, 12], inr: [0, 0.8, 1.1, 5],
      lipase: [0, 0, 60, 300], lactate: [0, 0.5, 2, 4], ph: [7.0, 7.35, 7.45, 7.7],
      troponin: [0, 0, 0.04, 1], trop: [0, 0, 0.04, 1], gfr: [15, 90, 999, 999],
    }
    const r = ranges[l]
    if (!r) return null
    const [critLow, low, high, critHigh] = r
    if (num <= critLow || num >= critHigh) return '#f87171'
    if (num < low || num > high) return '#fbbf24'
    return null
  }

  let html = ''

  // Vitals: stat cards
  if (subsections.vitals.length > 0) {
    let items: KVItem[] = []
    for (const line of subsections.vitals) {
      const parts = line.split(/,\s*(?![^()]*\))/)
      for (const p of parts) {
        const [k, v] = splitKV(p.trim())
        if (k) items.push({ label: vitalLabel(k), value: v, icon: vitalIcon(k) })
      }
    }
    items = mergeOrphans(items)

    html += `<p style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-dim); margin: 6px 0 6px 0; letter-spacing: 0.05em;">Vitals</p>`
    html += `<div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;">`
    for (const item of items) {
      html += `<div style="flex: 1 1 72px; max-width: 110px; min-width: 64px; background: var(--bg-deep, rgba(0,0,0,0.25)); border: 1px solid var(--border-subtle, rgba(255,255,255,0.08)); border-radius: 8px; padding: 6px 8px; text-align: center;">` +
        `<div style="font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-faint, #888); margin-bottom: 2px; white-space: nowrap;">${item.icon ? item.icon + ' ' : ''}${item.label}</div>` +
        `<div style="font-size: 15px; font-weight: 700; color: var(--text-primary, #fff); line-height: 1.15; white-space: nowrap;">${item.value || '\u2014'}</div>` +
        `</div>`
    }
    html += `</div>`
  }

  // Labs: panel-grouped grid with abnormal coloring
  if (subsections.labs.length > 0) {
    let items: KVItem[] = []
    for (const line of subsections.labs) {
      const parts = line.split(/,\s*(?![^()]*\))/)
      for (const p of parts) {
        const [k, v] = splitKV(p.trim())
        if (k) items.push({ label: k, value: v })
      }
    }
    items = items.filter(i => i.label && !/^\d+\)?$/.test(i.label))

    const panelOrder = ['CBC', 'BMP', 'LFT', 'UA', 'Other'] as const
    const panelLabels: Record<string, string> = {
      CBC: 'CBC', BMP: 'BMP / CMP', LFT: 'LFTs / Hepatic', UA: 'UA', Other: 'Other Labs',
    }
    const cbcKeys = /^(WBC|Hgb|Hb|Plt|RBC|MCV|RDW|Bands|Neut|Lymph)/i
    const bmpKeys = /^(Na|K|Cl|CO2|BUN|Cr|Glu|Glucose|Ca|Mg|Phos|GFR)/i
    const lftKeys = /^(AST|ALT|Alk|Tbili|Dbili|Bili|Albumin|Alb|TP|Lipase|INR)/i
    const uaKeys = /^(UA|SG|urobilinogen|pH|LE|nitrite|blood|protein|ketone|bilirubin|gravity)/i
    const UA_COMBINED_RE = /\b(LE|nitrite|blood|protein|ketone|bilirubin|urobilinogen|SG|gravity)\b/i

    const panels: Record<string, KVItem[]> = { CBC: [], BMP: [], LFT: [], UA: [], Other: [] }
    for (const item of items) {
      if (cbcKeys.test(item.label)) panels.CBC.push(item)
      else if (bmpKeys.test(item.label)) panels.BMP.push(item)
      else if (lftKeys.test(item.label)) panels.LFT.push(item)
      else if (uaKeys.test(item.label) || UA_COMBINED_RE.test(item.label)) panels.UA.push(item)
      else panels.Other.push(item)
    }

    html += `<p style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-dim); margin: 8px 0 4px 0; letter-spacing: 0.05em;">Labs</p>`
    html += `<div style="display: flex; flex-wrap: wrap; gap: 4px 16px; align-items: flex-start;">`

    for (const pk of panelOrder) {
      if (panels[pk].length === 0) continue
      html += `<div style="min-width: 100px; flex: 0 1 auto;">`
      html += `<div style="font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--accent-teal, #2dd4bf); margin-bottom: 2px; padding-left: 2px;">${panelLabels[pk]}</div>`
      for (const item of panels[pk]) {
        const color = labValueColor(item.label, item.value)
        const valStyle = color
          ? `font-weight: 700; color: ${color}; white-space: nowrap; font-variant-numeric: tabular-nums;`
          : `font-weight: 600; color: var(--text-primary, #fff); white-space: nowrap; font-variant-numeric: tabular-nums;`
        html += `<div style="display: flex; justify-content: space-between; align-items: baseline; padding: 1px 2px; font-size: 11px; border-bottom: 1px solid rgba(255,255,255,0.4); gap: 8px;">` +
          `<span style="color: var(--text-dim, #999); white-space: nowrap;">${item.label}</span>` +
          `<span style="${valStyle}">${item.value || '\u2014'}</span>` +
          `</div>`
      }
      html += `</div>`
    }

    html += `</div>`
  }

  // Diagnostics: styled card list
  if (subsections.imaging.length > 0) {
    html += `<p style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-dim); margin: 8px 0 4px 0; letter-spacing: 0.05em;">Diagnostics / Imaging</p>`
    html += `<div style="display: flex; flex-direction: column; gap: 3px; margin-bottom: 8px;">`
    for (const line of subsections.imaging) {
      html += `<div style="display: flex; align-items: flex-start; gap: 6px; font-size: 11px; line-height: 1.45; padding: 3px 6px; background: var(--bg-deep, rgba(0,0,0,0.15)); border-radius: 6px; border: 1px solid var(--border-faint, rgba(255,255,255,0.04));">` +
        `<span style="color: var(--accent-teal, #2dd4bf); flex-shrink: 0; font-size: 10px; margin-top: 1px;">\uD83D\uDCCB</span>` +
        `<span style="color: var(--text-secondary, #ccc);">${line}</span>` +
        `</div>`
    }
    html += `</div>`
  }

  // Anything that didn't fit
  if (subsections.other.length > 0) {
    html += `<div style="font-size: 12px; line-height: 1.5; margin-top: 6px;">${marked.parse(subsections.other.join('\n')) as string}</div>`
  }

  if (!html.trim()) {
    return marked.parse(content) as string
  }
  return html
}

/**
 * Render IPASS sections as styled HTML for the canvas.
 * Each section gets a colored left border, compact font, and tighter spacing.
 * The VD section gets special tabular rendering for vitals/labs.
 */
export const renderIPASSHtml = (sections: IPASSSections, _loc: LOCData | null = null): string => {
  const parts: string[] = []

  for (const key of IPASS_SECTIONS) {
    const label = SECTION_LABELS[key]
    const color = SECTION_COLORS[key]
    const content = sections[key]

    if (content) {
      const renderedContent = key === 'VD'
        ? renderVDTabular(content)
        : marked.parse(content) as string

      parts.push(
        `<div class="ipass-section" style="border-left: 3px solid ${color}; padding-left: 12px; margin-bottom: 20px;">` +
        `<h2 style="color: ${color}; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 6px 0; padding: 0; border: none;">${label}</h2>` +
        `<div style="font-size: 13px; line-height: 1.6;">${renderedContent}</div>` +
        `</div>`,
      )
    } else {
      parts.push(
        `<div class="ipass-section" style="border-left: 3px solid var(--text-faint); padding-left: 12px; margin-bottom: 20px; opacity: 0.5;">` +
        `<h2 style="color: var(--text-faint); font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 6px 0; padding: 0; border: none;">${label}</h2>` +
        `<p style="color: var(--text-dim); font-style: italic; font-size: 12px; margin: 0;">Pending...</p>` +
        `</div>`,
      )
    }
  }

  return parts.join('\n')
}

// ────────────────────────────────────────────────────────
// stripIPASSMarkers
// ────────────────────────────────────────────────────────

/**
 * Strip IPASS and LOC markers from text for chat display.
 */
export const stripIPASSMarkers = (text: string | null | undefined): string | null | undefined => {
  if (!text) return text
  return text
    .replace(/<!--\s*\/?IPASS:\w+\s*-->/gi, '')
    .replace(/<!--\s*LOC:\s*\{[\s\S]*?\}\s*-->/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
